/**
 * @typedef {Object} BaggageAllowance
 * @property {number|null} pieces - number of bags (piece-based allowance)
 * @property {number|null} weight - weight amount (weight-based allowance)
 * @property {string|null} weightUnit - weight unit code (e.g. "K", "L")
 * @property {string|null} description - carrier-provided allowance description
 *   (weight/size limits), may contain newlines
 */

const WEIGHT_UNIT_LABELS = { K: 'kg', KG: 'kg', L: 'lb', LB: 'lb' };

/**
 * Normalizes a baggage allowance into the {@link BaggageAllowance} object shape.
 * Tolerates the legacy numeric shape (a plain bag count) so a partial or mixed
 * API contract can't silently blank out baggage.
 * @param {BaggageAllowance|number|null} allowance
 * @returns {BaggageAllowance|null}
 */
const normalizeAllowance = (allowance) =>
  typeof allowance === 'number'
    ? { pieces: allowance, weight: null, weightUnit: null, description: null }
    : allowance;

/**
 * Formats the weight portion of a baggage allowance, e.g. "23kg".
 * @param {BaggageAllowance|number|null} rawAllowance
 * @returns {string|null}
 */
export const formatBaggageWeight = (rawAllowance) => {
  const allowance = normalizeAllowance(rawAllowance);
  if (allowance == null || allowance.weight == null || allowance.weight === 0) return null;
  const unit = (allowance.weightUnit || '').toUpperCase();
  const label = WEIGHT_UNIT_LABELS[unit] || (allowance.weightUnit || '').toLowerCase();
  return `${allowance.weight}${label}`;
};

/**
 * Whether the allowance contains any actual bags (pieces or weight).
 * @param {BaggageAllowance|number|null} rawAllowance
 * @returns {boolean}
 */
export const hasBaggage = (rawAllowance) => {
  const allowance = normalizeAllowance(rawAllowance);
  return (
    allowance != null &&
    ((allowance.pieces != null && allowance.pieces > 0) ||
      (allowance.weight != null && allowance.weight > 0))
  );
};

/**
 * Whether neither carry-on nor checked allowance contains any bags.
 * @param {BaggageAllowance|null} carryOn
 * @param {BaggageAllowance|null} checked
 * @returns {boolean}
 */
export const isNoBagsIncluded = (carryOn, checked) => !hasBaggage(carryOn) && !hasBaggage(checked);

const normalizedWeightUnitOf = (allowance) => {
  const unit = (allowance.weightUnit || '').toUpperCase();
  return WEIGHT_UNIT_LABELS[unit] || unit || null;
};

/**
 * Compares two baggage allowances for equality (pieces + weight + unit + description).
 * Missing fields (undefined) and null are treated as the same "absent" value.
 * @param {BaggageAllowance|number|null} rawA
 * @param {BaggageAllowance|number|null} rawB
 * @returns {boolean}
 */
export const baggageAllowanceEquals = (rawA, rawB) => {
  const a = normalizeAllowance(rawA);
  const b = normalizeAllowance(rawB);
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return (
    (a.pieces ?? null) === (b.pieces ?? null) &&
    (a.weight ?? null) === (b.weight ?? null) &&
    normalizedWeightUnitOf(a) === normalizedWeightUnitOf(b) &&
    (a.description || null) === (b.description || null)
  );
};

/**
 * Whether the baggage allowance differs between the given passenger infos
 * (entries of farePassengerInfo with carryOnBags/checkedBags). With `bagKey`
 * ("carryOnBags" | "checkedBags") only that bag type is compared — a label for
 * one bag type must not claim variance caused solely by the other type.
 * @param {Array<Object>} infos
 * @param {string} [bagKey]
 * @returns {boolean}
 */
export const baggageVariesByTraveler = (infos, bagKey) =>
  (infos || []).some((p) =>
    bagKey
      ? !baggageAllowanceEquals(p[bagKey], infos[0][bagKey])
      : !baggageAllowanceEquals(p.carryOnBags, infos[0].carryOnBags) ||
        !baggageAllowanceEquals(p.checkedBags, infos[0].checkedBags),
  );

/**
 * Returns the tooltip text for a baggage allowance: the carrier-provided
 * description when present (covers weight AND size limits), otherwise the
 * structured weight allowance sentence. Null when there is nothing to show.
 * @param {BaggageAllowance|number|null} rawAllowance
 * @param {string} typeLabel - e.g. "carry-on" or "checked"
 * @returns {string|null}
 */
export const getBaggageTooltipText = (rawAllowance, typeLabel) => {
  const allowance = normalizeAllowance(rawAllowance);
  if (allowance == null) return null;
  if (allowance.description) return allowance.description;
  const weightText = formatBaggageWeight(allowance);
  if (weightText) return `Weight allowance: ${weightText} per ${typeLabel} bag`;
  return null;
};

/**
 * Formats the base baggage label for an allowance: piece count when present
 * ("2 checked bags"), otherwise the weight allowance ("23kg checked") — never
 * both (no inline "(23kg)" suffix; that is formatSingleBaggageAllowance).
 * Returns null when there is no information about this allowance type.
 * @param {BaggageAllowance|number|null} rawAllowance
 * @param {string} typeLabel - e.g. "carry-on" or "checked"
 * @returns {string|null}
 */
export const formatBaggageBaseLabel = (rawAllowance, typeLabel) => {
  const allowance = normalizeAllowance(rawAllowance);
  if (allowance == null) return null;

  if (allowance.pieces != null && allowance.pieces > 0)
    return `${allowance.pieces} ${typeLabel} bag${allowance.pieces !== 1 ? 's' : ''}`;

  const weightText = formatBaggageWeight(allowance);
  if (weightText) return `${weightText} ${typeLabel}`;

  return `No ${typeLabel} bags`;
};

/**
 * Formats a single baggage allowance for a given type label.
 * Returns null when there is no information about this allowance type.
 * @param {BaggageAllowance|number|null} rawAllowance
 * @param {string} typeLabel - e.g. "carry-on" or "checked"
 * @returns {string|null}
 */
export const formatSingleBaggageAllowance = (rawAllowance, typeLabel) => {
  const allowance = normalizeAllowance(rawAllowance);
  const base = formatBaggageBaseLabel(allowance, typeLabel);
  if (base == null) return null;

  const weightText = formatBaggageWeight(allowance);
  if (weightText && allowance.pieces != null && allowance.pieces > 0)
    return `${base} (${weightText})`;

  return base;
};

/**
 * Returns a human-readable baggage string for a single passenger type.
 * @param {BaggageAllowance|null} carryOn - carry-on baggage allowance
 * @param {BaggageAllowance|null} checked - checked baggage allowance
 * @returns {string}
 */
const formatBaggageForPassenger = (carryOn, checked) => {
  const parts = [];
  const carryOnText = formatSingleBaggageAllowance(carryOn, 'carry-on');
  if (carryOnText != null) parts.push(carryOnText);
  const checkedText = formatSingleBaggageAllowance(checked, 'checked');
  if (checkedText != null) parts.push(checkedText);
  return parts.join(', ');
};

export default formatBaggageForPassenger;
